import { createColumnHelper } from '@tanstack/react-table';
import { useLocalStorage } from '../../Common/components/hooks/useLocalStorage';
import { Trash2 } from 'lucide-react';
import Table from '../../Common/components/Table';
import { Switch } from '@/src/shadComponents/ui/switch';
import { Label } from '@/src/shadComponents/ui/label';
import NonTrackingGalleryImage from '@/src/Common/components/NonTrackingGalleryImage';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';
interface PhotoManagerListProps {
    shares: (ShareObject & MediaInstance)[];
    reloadShares: () => void;
}

const columnHelper = createColumnHelper<ShareObject & MediaInstance>();

export default function ShareList({
    shares,
    reloadShares,
}: PhotoManagerListProps) {
    const [showPhotoPreview, setShowPhotoPreview] = useLocalStorage(
        'sb-pref-show-share-photo-preview',
        true,
    );
    const columnDefinitions = [
        columnHelper.accessor('originalUri', {
            id: 'originalUri',
            header: () => <span>photo</span>,
            enableColumnFilter: false,
            cell: (info) => (
                <div className="row">
                    <div className="image-preview">
                        {showPhotoPreview ? (
                            info.row.original.thumbnailUri ? (
                                <div className="w-[100px]">
                                    <NonTrackingGalleryImage
                                        media={info.row.original}
                                    />
                                </div>
                            ) : (
                                <span className="text-red-500 font-bold">
                                    Missing
                                </span>
                            )
                        ) : info.row.original.originalUri ? (
                            <a
                                href={info.row.original.originalUri}
                                target="_blank"
                                rel="noreferrer"
                            >
                                [link]
                            </a>
                        ) : (
                            <span className="text-red-500 font-bold">
                                Missing
                            </span>
                        )}
                    </div>
                </div>
            ),
        }),
        columnHelper.accessor('destination', {
            id: 'destination',
            filterFn: 'fuzzy',
            header: () => <span>destination</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue()}</span>
                </div>
            ),
        }),
        columnHelper.accessor('type', {
            id: 'type',
            header: () => <span>type</span>,
            cell: (info) => {
                const typeLabels: { [key: number]: string } = {
                    0: 'Email',
                    1: 'Text',
                    2: 'WhatsApp',
                };
                const value = info.getValue();
                return (
                    <div className="row">
                        <span>{typeLabels[value as number] ?? value}</span>
                    </div>
                );
            },
        }),
        columnHelper.accessor('sent', {
            id: 'sent',
            header: () => <span>sent</span>,
            cell: (info) => (
                <div className="row">
                    <span>{info.getValue() ? 'yes' : 'no'}</span>
                </div>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props: { row: any }) => (
                <ShareActions
                    onDelete={() => {
                        console.log('delete');
                        reloadShares();
                    }}
                    row={props.row}
                />
            ),
        }),
    ];

    return (
        <div>
            <span className="pl-7 flex items-center gap-2">
                <Switch
                    id="show-archived"
                    onCheckedChange={() =>
                        setShowPhotoPreview((preview: boolean) => !preview)
                    }
                    checked={showPhotoPreview}
                />
                <Label htmlFor="show-archived">
                    Display Preview
                </Label>
            </span>
            <Table
                data={shares}
                columns={columnDefinitions}
                enableSorting={true}
                enablePagination={true}
            />
        </div>
    );
}

export function ShareActions({ row, onDelete }: any) {
    const shareId = row.original.id;
    const authenticatedApi = useAuthenticatedApi();
    const { orgId } = useUserOrg();

    const performDelete = () => {
        const deleteRequest = async () => {
            const response = await authenticatedApi.delete(
                `${orgId}/shares/share/${shareId}`,
            );

            if (response.status === 200) {
                onDelete();
            }
        };

        const shouldDelete = window.confirm(
            `Are you sure you would like to delete this share?`,
        );

        if (shouldDelete) {
            deleteRequest();
        }
    };
    return (
        <span className="Table-options">
            <Trash2 onClick={performDelete} className="size-4 cursor-pointer" />
        </span>
    );
}
